import axios from 'axios';

// Configure axios base URL
const envApiBaseUrl = process.env.REACT_APP_API_URL;
const isLocalFrontend =
  typeof window !== 'undefined' &&
  window.location.hostname === 'localhost' &&
  window.location.port === '3000';

const API_BASE_URL =
  isLocalFrontend && envApiBaseUrl === '/api/v1'
    ? 'http://localhost:8000/api/v1'
    : envApiBaseUrl || 'http://localhost:8000/api/v1';

const TOKEN_REFRESH_THRESHOLD_MS = 15 * 60 * 1000;

const api = axios.create({
  baseURL: API_BASE_URL,
});

let refreshTokenRequest = null;

const decodeJwtPayload = (token) => {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(window.atob(paddedBase64));
  } catch (error) {
    return null;
  }
};

const getTokenExpirationMs = (token) => {
  const payload = decodeJwtPayload(token);
  return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
};

const shouldRefreshToken = (token) => {
  const expiresAt = getTokenExpirationMs(token);
  if (!expiresAt) return false;

  const timeLeft = expiresAt - Date.now();
  return timeLeft > 0 && timeLeft <= TOKEN_REFRESH_THRESHOLD_MS;
};

const refreshAccessToken = async (token) => {
  if (!refreshTokenRequest) {
    refreshTokenRequest = axios.post(
      `${API_BASE_URL}/auth/refresh`,
      null,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )
      .then((response) => {
        const refreshedToken = response.data?.access_token;
        if (refreshedToken) {
          localStorage.setItem('token', refreshedToken);
        }
        return refreshedToken;
      })
      .finally(() => {
        refreshTokenRequest = null;
      });
  }

  return refreshTokenRequest;
};

api.interceptors.request.use(
  async (config) => {
    if (config.url?.includes('/auth/jwt/login') || config.url?.includes('/auth/refresh')) {
      return config;
    }

    const token = localStorage.getItem('token');
    if (!token || !shouldRefreshToken(token)) {
      return config;
    }

    try {
      const refreshedToken = await refreshAccessToken(token);
      if (refreshedToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${refreshedToken}`;
      }
    } catch (error) {
      localStorage.removeItem('token');
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 401 || status === 403) {
      localStorage.removeItem('token');
      if (typeof window !== 'undefined' && window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  },
);

// Auth service
export const authService = {
  // Login function - returns token and user info
  login: async (email, password) => {
    try {
      // FastAPI Users expects application/x-www-form-urlencoded for JWT login.
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await api.post('/auth/jwt/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const { access_token, token_type } = response.data;

      // Store token
      localStorage.setItem('token', access_token);

      // Get user info to check role
      const userInfo = await authService.getCurrentUser(access_token);

      return {
        success: true,
        token: access_token,
        user: userInfo,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  },

  // Get current user info
  getCurrentUser: async (token) => {
    try {
      const response = await api.get('/auth/users/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw new Error('Failed to get user info');
    }
  },

  // Logout function
  logout: () => {
    localStorage.removeItem('token');
  },

  // Check if user has admin role
  isAdmin: (user) => {
    return user?.is_active === true && user?.role === 'superuser' && user?.is_superuser === true;
  },

  // Get stored token
  getToken: () => {
    return localStorage.getItem('token');
  },
};

export default api;
